import React, { useMemo } from 'react';
import {
    FaExchangeAlt, FaWater, FaCheckCircle, FaExclamationTriangle, FaInfoCircle
} from 'react-icons/fa';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
    Sankey, Rectangle, Layer
} from 'recharts';

const CashFlowTab = ({
    L, CHART_COLORS, LIGHT_TOOLTIP,
    assets, liabilities, totalAssets, totalLiabilities, netWorth,
    transactions, fixedExpenses, emis, operationalExpenses,
    calendarMode, selectedDate, dateRange,
    wishlistItems,
}) => {
    const selectedMonth = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;

    // Period income
    const periodIncome = useMemo(() => {
        return (transactions || []).filter(t => {
            const isCredit = t.type === 'credit' || t.type === 'income' || t.type === 'gain';
            if (!isCredit) return false;
            const txDate = new Date(t.date);
            return txDate >= dateRange.start && txDate <= dateRange.end;
        });
    }, [transactions, dateRange]);

    const totalIncome = useMemo(() =>
        periodIncome.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0),
    [periodIncome]);

    // Period expenses from transactions
    const periodExpenseTxns = useMemo(() => {
        return (transactions || []).filter(t => {
            const isDebit = t.type !== 'credit' && t.type !== 'income' && t.type !== 'gain';
            if (!isDebit) return false;
            const txDate = new Date(t.date);
            return txDate >= dateRange.start && txDate <= dateRange.end;
        });
    }, [transactions, dateRange]);

    // Period operational expenses
    const periodOps = useMemo(() => {
        return (operationalExpenses || []).filter(e => {
            const d = new Date(e.date);
            return d >= dateRange.start && d <= dateRange.end;
        });
    }, [operationalExpenses, dateRange]);

    // Paid fixed + EMI for selected month
    const paidFixedTotal = useMemo(() =>
        (fixedExpenses || []).filter(e => {
            return e.isActive !== false && (e.monthlyStatus || {})[selectedMonth] === 'paid';
        }).reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [fixedExpenses, selectedMonth]);

    const paidEMITotal = useMemo(() =>
        (emis || []).filter(e => (e.monthlyStatus || {})[selectedMonth] === 'paid')
            .reduce((s, e) => s + (Number(e.monthlyAmount) || 0), 0),
    [emis, selectedMonth]);

    // Period one-time payments (wishlist payments within dateRange)
    const periodOneTimeTotal = useMemo(() => {
        return (wishlistItems || []).reduce((sum, item) => {
            return sum + (item.payments || []).filter(p => {
                const d = new Date(p.date);
                return d >= dateRange.start && d <= dateRange.end;
            }).reduce((s, p) => s + (Number(p.amount) || 0), 0);
        }, 0);
    }, [wishlistItems, dateRange]);

    const totalExpenses = useMemo(() => {
        const txnTotal = periodExpenseTxns.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
        const opTotal = periodOps.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        return txnTotal + opTotal + paidFixedTotal + paidEMITotal + periodOneTimeTotal;
    }, [periodExpenseTxns, periodOps, paidFixedTotal, paidEMITotal, periodOneTimeTotal]);

    const netCashFlow = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? ((netCashFlow / totalIncome) * 100) : 0;
    const expenseToIncomeRatio = totalIncome > 0 ? ((totalExpenses / totalIncome) * 100) : 0;

    // Pie data
    const pieData = useMemo(() => [
        { name: 'Income', value: totalIncome || 1 },
        { name: 'Expenses', value: totalExpenses || 1 },
    ], [totalIncome, totalExpenses]);

    const PIE_COLORS = ['#10B981', '#EF4444'];

    // Asset vs Liability data
    const balanceData = useMemo(() => {
        const assetsByType = {};
        (assets || []).forEach(a => {
            const t = a.assetType || 'Other';
            assetsByType[t] = (assetsByType[t] || 0) + (Number(a.value) || 0);
        });
        const liabByType = {};
        (liabilities || []).forEach(l => {
            const t = l.liabilityType || 'Other';
            liabByType[t] = (liabByType[t] || 0) + (Number(l.amount) || 0);
        });
        return {
            assets: Object.entries(assetsByType).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
            liabilities: Object.entries(liabByType).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
        };
    }, [assets, liabilities]);

    // Sankey data
    const sankeyData = useMemo(() => {
        const nodes = [];
        const links = [];
        const nodeMap = {};
        let idx = 0;

        // Income sources
        const incomeByCat = {};
        periodIncome.forEach(t => {
            const cat = t.category || 'Other Income';
            incomeByCat[cat] = (incomeByCat[cat] || 0) + Math.abs(Number(t.amount) || 0);
        });
        const incomeEntries = Object.entries(incomeByCat).filter(([, v]) => v > 0);

        if (incomeEntries.length === 0 && totalExpenses === 0) return null;

        // If no categorized income, add a generic one
        if (incomeEntries.length === 0 && totalIncome > 0) {
            incomeEntries.push(['Income', totalIncome]);
        }

        incomeEntries.forEach(([cat]) => {
            nodeMap[`inc_${cat}`] = idx;
            nodes.push({ name: cat });
            idx++;
        });

        // Total Income node
        nodeMap['total_income'] = idx;
        nodes.push({ name: 'Total Income' });
        idx++;

        // Fixed expenses (paid only for accurate cash flow)
        const totalFixed = paidFixedTotal;
        const totalEMI = paidEMITotal;
        const totalOp = periodOps.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const totalTxnExpense = periodExpenseTxns.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);

        // Expense category nodes
        if (totalFixed > 0) {
            nodeMap['fixed'] = idx;
            nodes.push({ name: 'Fixed Expenses' });
            idx++;
        }
        if (totalEMI > 0) {
            nodeMap['emis'] = idx;
            nodes.push({ name: 'EMIs' });
            idx++;
        }
        if (totalOp > 0) {
            nodeMap['operational'] = idx;
            nodes.push({ name: 'Operational' });
            idx++;
        }
        if (totalTxnExpense > 0) {
            nodeMap['bank_expenses'] = idx;
            nodes.push({ name: 'Bank Expenses' });
            idx++;
        }
        if (periodOneTimeTotal > 0) {
            nodeMap['onetime'] = idx;
            nodes.push({ name: 'One-Time' });
            idx++;
        }

        // Fixed sub-categories (paid only)
        const fixedByCat = {};
        (fixedExpenses || []).filter(e => e.isActive !== false && (e.monthlyStatus || {})[selectedMonth] === 'paid').forEach(e => {
            const cat = e.category || 'Other';
            fixedByCat[cat] = (fixedByCat[cat] || 0) + (Number(e.amount) || 0);
        });
        Object.entries(fixedByCat).filter(([, v]) => v > 0).forEach(([cat]) => {
            nodeMap[`fixed_${cat}`] = idx;
            nodes.push({ name: cat });
            idx++;
        });

        // Operational sub-categories
        const opByCat = {};
        periodOps.forEach(e => {
            const cat = e.category || 'Other';
            opByCat[cat] = (opByCat[cat] || 0) + (Number(e.amount) || 0);
        });
        Object.entries(opByCat).filter(([, v]) => v > 0).forEach(([cat]) => {
            if (!nodeMap[`op_${cat}`]) {
                nodeMap[`op_${cat}`] = idx;
                nodes.push({ name: `${cat} (Op)` });
                idx++;
            }
        });

        // Terminal: Savings or Deficit
        const surplus = totalIncome - totalExpenses;
        if (surplus > 0) {
            nodeMap['savings'] = idx;
            nodes.push({ name: 'Savings' });
            idx++;
        } else if (surplus < 0) {
            // Add deficit as a source node (funded by savings/debt)
            nodeMap['deficit_source'] = idx;
            nodes.push({ name: 'Funded by Debt' });
            idx++;
        }

        // Links: income sources -> Total Income
        incomeEntries.forEach(([cat, val]) => {
            links.push({ source: nodeMap[`inc_${cat}`], target: nodeMap['total_income'], value: val });
        });

        // If deficit, add funding link
        if (surplus < 0 && nodeMap['deficit_source'] !== undefined) {
            links.push({ source: nodeMap['deficit_source'], target: nodeMap['total_income'], value: Math.abs(surplus) });
        }

        // Total Income -> expense categories
        if (totalFixed > 0 && nodeMap['fixed'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['fixed'], value: totalFixed });
        if (totalEMI > 0 && nodeMap['emis'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['emis'], value: totalEMI });
        if (totalOp > 0 && nodeMap['operational'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['operational'], value: totalOp });
        if (totalTxnExpense > 0 && nodeMap['bank_expenses'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['bank_expenses'], value: totalTxnExpense });
        if (periodOneTimeTotal > 0 && nodeMap['onetime'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['onetime'], value: periodOneTimeTotal });

        // Savings link
        if (surplus > 0 && nodeMap['savings'] !== undefined) links.push({ source: nodeMap['total_income'], target: nodeMap['savings'], value: surplus });

        // Fixed -> sub-categories
        Object.entries(fixedByCat).filter(([, v]) => v > 0).forEach(([cat, val]) => {
            if (nodeMap['fixed'] !== undefined && nodeMap[`fixed_${cat}`] !== undefined) {
                links.push({ source: nodeMap['fixed'], target: nodeMap[`fixed_${cat}`], value: val });
            }
        });

        // Operational -> sub-categories
        Object.entries(opByCat).filter(([, v]) => v > 0).forEach(([cat, val]) => {
            if (nodeMap['operational'] !== undefined && nodeMap[`op_${cat}`] !== undefined) {
                links.push({ source: nodeMap['operational'], target: nodeMap[`op_${cat}`], value: val });
            }
        });

        // Filter zero-value links
        const validLinks = links.filter(l => l.value > 0 && l.source !== undefined && l.target !== undefined && l.source !== l.target);

        if (nodes.length < 2 || validLinks.length === 0) return null;

        return { nodes, links: validLinks, _nodeMap: nodeMap };
    }, [periodIncome, periodExpenseTxns, periodOps, fixedExpenses, emis, totalIncome, totalExpenses, paidFixedTotal, paidEMITotal, periodOneTimeTotal, selectedMonth]);

    // Node color mapping
    const getNodeColor = (name) => {
        if (name === 'Total Income') return '#3B82F6';
        if (name === 'Savings') return '#10B981';
        if (name === 'Funded by Debt') return '#EF4444';
        if (name === 'Fixed Expenses') return '#F59E0B';
        if (name === 'EMIs') return '#EC4899';
        if (name === 'Operational') return '#8B5CF6';
        if (name === 'Bank Expenses') return '#EF4444';
        if (name === 'One-Time') return '#6366F1';
        // Income sources
        if (sankeyData?._nodeMap && Object.keys(sankeyData._nodeMap).some(k => k.startsWith('inc_') && sankeyData._nodeMap[k] !== undefined)) {
            const incKeys = Object.keys(sankeyData._nodeMap).filter(k => k.startsWith('inc_'));
            const incNames = incKeys.map(k => sankeyData.nodes[sankeyData._nodeMap[k]]?.name);
            if (incNames.includes(name)) return '#06B6D4';
        }
        return '#94A3B8';
    };

    // Custom Sankey node
    const CustomSankeyNode = ({ x, y, width, height, index, payload }) => {
        const fill = getNodeColor(payload.name);
        const isRight = x > 300;
        return (
            <Layer key={`node-${index}`}>
                <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.85} radius={[4, 4, 4, 4]} />
                <text
                    x={isRight ? x - 6 : x + width + 6}
                    y={y + height / 2}
                    textAnchor={isRight ? 'end' : 'start'}
                    dominantBaseline="middle"
                    fill="#374151" fontSize={11} fontWeight={500}
                >
                    {payload.name}
                </text>
            </Layer>
        );
    };

    // Health guidance
    const healthGuidance = useMemo(() => {
        if (totalIncome === 0 && totalExpenses === 0) return { level: 'neutral', message: 'No cash flow data for this period.', icon: <FaInfoCircle /> };
        if (savingsRate >= 50) return { level: 'excellent', message: 'Excellent! You are saving more than half your income.', icon: <FaCheckCircle /> };
        if (savingsRate >= 25) return { level: 'good', message: 'Good. Healthy savings ratio. Consider investing the surplus.', icon: <FaCheckCircle /> };
        if (savingsRate >= 10) return { level: 'fair', message: 'Fair. Consider reducing discretionary spending to build reserves.', icon: <FaExclamationTriangle /> };
        if (savingsRate >= 0) return { level: 'warning', message: 'Thin margin. Review fixed costs and cut non-essentials.', icon: <FaExclamationTriangle /> };
        return { level: 'critical', message: `Deficit of \u20B9${Math.abs(netCashFlow).toLocaleString()}. Expenses exceed income. Immediate action needed.`, icon: <FaExclamationTriangle /> };
    }, [totalIncome, totalExpenses, savingsRate, netCashFlow]);

    const healthColor = {
        excellent: 'bg-emerald-50 border-emerald-200 text-emerald-700',
        good: 'bg-blue-50 border-blue-200 text-blue-700',
        fair: 'bg-amber-50 border-amber-200 text-amber-700',
        warning: 'bg-orange-50 border-orange-200 text-orange-700',
        critical: 'bg-rose-50 border-rose-200 text-rose-700',
        neutral: 'bg-gray-50 border-gray-200 text-gray-500',
    };

    const totalFixedPlusEMI = paidFixedTotal + paidEMITotal + periodOneTimeTotal;
    const fixedCostRatio = totalIncome > 0 ? ((totalFixedPlusEMI / totalIncome) * 100) : 0;

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={L.card}>
                    <p className={L.textLabel}>Income</p>
                    <p className={`text-2xl font-bold ${L.positive} mt-1`}>{'\u20B9'}{totalIncome.toLocaleString()}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Expenses</p>
                    <p className={`text-2xl font-bold ${L.negative} mt-1`}>{'\u20B9'}{totalExpenses.toLocaleString()}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Net Cash Flow</p>
                    <p className={`text-2xl font-bold ${netCashFlow >= 0 ? L.positive : L.negative} mt-1`}>
                        {netCashFlow < 0 ? '-' : '+'}{'\u20B9'}{Math.abs(netCashFlow).toLocaleString()}
                    </p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Savings Rate</p>
                    <p className={`text-2xl font-bold ${savingsRate >= 25 ? L.positive : savingsRate >= 0 ? 'text-amber-600' : L.negative} mt-1`}>
                        {savingsRate.toFixed(1)}%
                    </p>
                </div>
            </div>

            {/* Income vs Expense Pie + Balance Sheet */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className={L.card}>
                    <h3 className={`text-base font-bold ${L.text} mb-4`}>Income vs Expenses</h3>
                    <div className="flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4}>
                                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                </Pie>
                                <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-2">
                        <span className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                            Income ({totalIncome > 0 ? ((totalIncome / (totalIncome + totalExpenses)) * 100).toFixed(0) : 0}%)
                        </span>
                        <span className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                            <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                            Expenses ({totalExpenses > 0 ? ((totalExpenses / (totalIncome + totalExpenses)) * 100).toFixed(0) : 0}%)
                        </span>
                    </div>
                </div>

                {/* Balance Sheet */}
                <div className={L.card}>
                    <h3 className={`text-base font-bold ${L.text} mb-4`}>Assets vs Liabilities</h3>
                    <div className="space-y-4">
                        {/* Summary bar */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-semibold text-blue-600">Assets</span>
                                    <span className={`text-xs font-bold ${L.text}`}>{'\u20B9'}{totalAssets.toLocaleString()}</span>
                                </div>
                                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{ width: `${totalAssets + totalLiabilities > 0 ? (totalAssets / (totalAssets + totalLiabilities)) * 100 : 50}%` }} />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-semibold text-rose-600">Liabilities</span>
                                    <span className={`text-xs font-bold ${L.text}`}>{'\u20B9'}{totalLiabilities.toLocaleString()}</span>
                                </div>
                                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-rose-500 to-rose-400 rounded-full" style={{ width: `${totalAssets + totalLiabilities > 0 ? (totalLiabilities / (totalAssets + totalLiabilities)) * 100 : 50}%` }} />
                                </div>
                            </div>
                        </div>

                        {/* Breakdown */}
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                            <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2">Assets</p>
                                {balanceData.assets.map((a, i) => (
                                    <div key={a.name} className="flex items-center justify-between text-xs mb-1.5">
                                        <span className="text-gray-600">{a.name}</span>
                                        <span className="font-semibold text-gray-900">{'\u20B9'}{a.value.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2">Liabilities</p>
                                {balanceData.liabilities.map((l, i) => (
                                    <div key={l.name} className="flex items-center justify-between text-xs mb-1.5">
                                        <span className="text-gray-600">{l.name}</span>
                                        <span className="font-semibold text-gray-900">{'\u20B9'}{l.value.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-3 bg-blue-50 rounded-xl text-center">
                            <p className="text-[10px] text-blue-400 uppercase tracking-widest">Net Worth</p>
                            <p className={`text-xl font-bold ${netWorth >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>{'\u20B9'}{netWorth.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sankey Flow Diagram */}
            <div className={L.card}>
                <div className="flex items-center gap-3 mb-4">
                    <FaWater className="text-blue-500" />
                    <h3 className={`text-base font-bold ${L.text}`}>Cash Flow</h3>
                </div>
                {sankeyData ? (
                    <div className="overflow-x-auto">
                        <div style={{ minWidth: 700 }}>
                            <Sankey
                                width={700}
                                height={Math.max(350, (sankeyData.nodes.length * 30))}
                                data={{ nodes: sankeyData.nodes, links: sankeyData.links }}
                                nodeWidth={12}
                                nodePadding={16}
                                linkCurvature={0.5}
                                node={<CustomSankeyNode />}
                                link={{ stroke: '#CBD5E1', strokeOpacity: 0.4 }}
                                margin={{ left: 10, right: 150, top: 10, bottom: 10 }}
                            >
                                <Tooltip
                                    formatter={(value) => `\u20B9${Number(value).toLocaleString()}`}
                                    contentStyle={LIGHT_TOOLTIP}
                                />
                            </Sankey>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <FaExchangeAlt className="text-3xl text-gray-300 mx-auto mb-3" />
                        <p className={`text-sm ${L.textMuted}`}>Add income and expense data to see the cash flow</p>
                    </div>
                )}

                {/* Flow Legend */}
                {sankeyData && (
                    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-cyan-500"></span> Income Sources</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-blue-500"></span> Total Income</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-amber-500"></span> Fixed</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-pink-500"></span> EMIs</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-violet-500"></span> Operational</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-indigo-500"></span> One-Time</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-emerald-500"></span> Savings</span>
                        <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-rose-500"></span> Deficit</span>
                    </div>
                )}
            </div>

            {/* Cash Flow Analytics */}
            <div className={L.card}>
                <h3 className={`text-base font-bold ${L.text} mb-4`}>Cash Flow Analytics</h3>

                {/* Key Ratios */}
                <div className="grid grid-cols-3 gap-4 mb-5">
                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest">Savings Rate</p>
                        <p className={`text-xl font-bold ${savingsRate >= 25 ? 'text-emerald-600' : savingsRate >= 0 ? 'text-amber-600' : 'text-rose-600'} mt-1`}>
                            {savingsRate.toFixed(1)}%
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest">Expense/Income</p>
                        <p className={`text-xl font-bold ${expenseToIncomeRatio <= 75 ? 'text-emerald-600' : expenseToIncomeRatio <= 100 ? 'text-amber-600' : 'text-rose-600'} mt-1`}>
                            {expenseToIncomeRatio.toFixed(0)}%
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest">Fixed Cost Ratio</p>
                        <p className={`text-xl font-bold ${fixedCostRatio <= 40 ? 'text-emerald-600' : fixedCostRatio <= 60 ? 'text-amber-600' : 'text-rose-600'} mt-1`}>
                            {fixedCostRatio.toFixed(0)}%
                        </p>
                    </div>
                </div>

                {/* Health Guidance */}
                <div className={`p-4 rounded-xl border ${healthColor[healthGuidance.level]}`}>
                    <div className="flex items-start gap-3">
                        <span className="text-lg mt-0.5">{healthGuidance.icon}</span>
                        <div>
                            <p className="text-sm font-semibold">{healthGuidance.message}</p>
                            {netCashFlow > 0 && (
                                <p className="text-xs mt-1 opacity-80">
                                    Consider: Invest surplus in assets, increase SIP contributions, or build an emergency fund.
                                </p>
                            )}
                            {netCashFlow < 0 && (
                                <p className="text-xs mt-1 opacity-80">
                                    Action: Review top spending categories, reduce subscriptions, renegotiate fixed costs, or increase income sources.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CashFlowTab;
